import { useEffect, useState } from 'react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useChatAPI } from '../../hooks/useChatAPI';
import { useChatMessages } from '../../hooks/useChatMessages';
import { useConversationContext } from '../../hooks/useConversationContext';
import { useMiniAppModal } from '../../hooks/useMiniAppModal';
import { useMiniApps } from '../../hooks/useMiniApps';
import { useOrderTracking } from '../../hooks/useOrderTracking';
import { useStreamingResponse } from '../../hooks/useStreamingResponse';
import { useThemeSettings } from '../../hooks/useThemeSettings';
import { useCustomTexts } from '../../hooks/useCustomTexts';
import { useQuickActions } from '../../hooks/useQuickActions';
import { generateUUID } from '../../utils/uuid';
import { MiniAppButton } from './MiniAppButton';
import { QuickActionButton } from './QuickActionButton';
import { SocialMediaButtons } from './SocialMediaButtons';
import { OrderTrackingInput } from './OrderTrackingInput';
import { useI18n } from '../../i18n';

export function QuickActionsButtons() {
  const { chatbotPublicId } = useChatbot();
  const { actions, initializeQuickActions } = useQuickActions();
  const { miniApps } = useMiniApps();
  const { addMessage, updateLastBotMessage } = useChatMessages();
  const { sendMessage } = useChatAPI();
  const { streamResponse, isStreaming } = useStreamingResponse();
  const { addToContext } = useConversationContext();
  const { theme } = useThemeSettings();
  const { openModal } = useMiniAppModal();
  const { settings: orderTrackingSettings, initialized: orderTrackingInitialized, trackOrder, formatResponse } = useOrderTracking();
  const [waitingForOrderNumber, setWaitingForOrderNumber] = useState(false);
  const [orderTrackingMessageId, setOrderTrackingMessageId] = useState<string | null>(null);
  const [messageFullyTyped, setMessageFullyTyped] = useState(false);
  const { language: currentLanguage } = useI18n();
  
  // Helper function to get translations
  const t = (key: string) => {
    const keys = key.split('.');
    let value: any = currentLanguage === 'en' ? require('../../i18n/translations/en').en 
                    : currentLanguage === 'fr' ? require('../../i18n/translations/fr').fr 
                    : require('../../i18n/translations/pt').pt;

    for (const k of keys) {
      if (!value || !value[k]) return key;
      value = value[k];
    }
    return value;
  };
  
  // Initialize quick actions from database when component mounts
  useEffect(() => {
    if (chatbotPublicId) {
      initializeQuickActions(chatbotPublicId);
    }
  }, [chatbotPublicId, initializeQuickActions]);
  
  // Log when order tracking settings change
  useEffect(() => {
    if (orderTrackingSettings) {
      // Commenté pour réduire les logs excessifs
      /*console.log('Order tracking settings updated:', {
        enabled: orderTrackingSettings.enabled,
        buttonVisible: orderTrackingSettings.buttonVisible,
        type: typeof orderTrackingSettings.buttonVisible
      });*/
    }
  }, [orderTrackingSettings]);
  
  // Filter out the order-tracking mini-app because we handle it differently
  const visibleApps = miniApps.filter(app => 
    app.enabled && 
    app.id !== 'order-tracking' && 
    app.buttonVisible !== false
  );
  
  // Get the Order Tracking mini-app if it exists
  const orderTrackingApp = miniApps.find(app => app.id === 'order-tracking');
  
  // Log the raw values to debug - Commenté pour réduire les logs
  /*console.log('Order Tracking Debug:', {
    rawApp: orderTrackingApp,
    rawButtonVisible: orderTrackingApp?.buttonVisible,
    typeOfButtonVisible: typeof orderTrackingApp?.buttonVisible,
    miniAppsState: miniApps
  });*/
  
  // Determine if we should show the order tracking button
  const showOrderTracking = orderTrackingApp?.enabled === true &&
                            orderTrackingSettings?.enabled === true;
  
  // Utiliser une référence pour éviter des mutations inutiles d'objet
  const allActions = [...actions];
  
  // Ajouter le bouton de suivi de commande si nécessaire
  if (showOrderTracking) {
    const orderTrackingText = currentLanguage !== 'en' 
      ? t('orderTracking.quickActionText')
      : (orderTrackingSettings?.quick_action_text || 'Track my order');

    allActions.push({ text: orderTrackingText });
  }

  // Check if social media mini-app is enabled
  const socialMediaApp = miniApps.find(app => app.id === 'social');
  const socialMediaEnabled = socialMediaApp && socialMediaApp.enabled === true;
  
  // Commenté pour réduire les logs excessifs
  /*console.log('Social media mini-app status in QuickActionsButtons:', {
    enabled: socialMediaEnabled,
    visibleApps,
    socialApp: socialMediaApp
  });*/

  const handleQuickAction = async (text: string, url?: string) => {
    if (url) {
      window.open(url, '_blank');
      return;
    }

    // If the action is "Search product", open the mini-app Products
    if (text.toLowerCase().includes('search product')) {
      openModal('products');
      return;
    }
    
    // If the action is the order tracking action, handle it specially
    if (orderTrackingInitialized && 
        orderTrackingSettings?.enabled && 
        text === orderTrackingSettings.quick_action_text) {
      handleOrderTracking();
      return;
    }

    const userMessage = {
      id: generateUUID(),
      type: 'user' as const,
      content: text,
      timestamp: new Date().toISOString()
    };

    addMessage(userMessage);
    addToContext(userMessage);

    try {
      const stream = await sendMessage(text);
      
      await streamResponse(stream, (botMessage) => {
        addToContext(botMessage);
      });
    } catch (error) {
      console.error('Error sending message:', error);
      const { errorMessage } = useCustomTexts.getState();
      addMessage({
        type: 'bot',
        content: errorMessage
      });
    }
  };
  
  // Handle order tracking flow
  const handleOrderTracking = async () => {
    // Reset the fully typed state
    setMessageFullyTyped(false);
    
    // First, send a message asking for the order number
    const messageId = generateUUID();
    
    // Get the prompt message from translations or settings or use default
    const promptMessage = currentLanguage !== 'en'
      ? t('orderTracking.promptMessage')
      : (orderTrackingSettings?.prompt_message || 'Please enter your order number to track your order:');
    
    // Add a message first
    addMessage({
      type: 'bot',
      content: ''
    });
    
    // Simulate typing effect
    const words = promptMessage.split(' ');
    let currentText = '';
    
    for (let i = 0; i < words.length; i++) {
      currentText += (i > 0 ? ' ' : '') + words[i];
      updateLastBotMessage(currentText);
      await new Promise(resolve => setTimeout(resolve, 50)); // Delay between words
    }
    
    // Mark message as fully typed
    setMessageFullyTyped(true);
    
    // Add to context
    addToContext({
      id: messageId,
      type: 'bot',
      content: promptMessage,
      timestamp: new Date().toISOString()
    });
    
    // Set waiting flag to true and store the message ID
    setWaitingForOrderNumber(true);
    setOrderTrackingMessageId(messageId);
  };
  
  // Process the order number and respond with tracking info
  const processOrderNumber = async (orderNumber: string) => {
    if (!orderNumber || orderNumber.trim().length === 0) return;
    
    // Clean up the order number
    const cleanOrderNumber = orderNumber.trim();
    
    // Show loading message
    const loadingText = "Looking up your order";
    const loadingDots = [".", "..", "..."];
    let dotIndex = 0;
    
    // Add the user's message to context
    addToContext({
      id: generateUUID(),
      type: 'user',
      content: cleanOrderNumber,
      timestamp: new Date().toISOString()
    });
    
    // Add a message from the bot that will be updated
    const botMessageId = generateUUID();
    addToContext({
      id: botMessageId,
      type: 'bot',
      content: loadingText,
      timestamp: new Date().toISOString()
    });
    
    // Add a loading message to the UI
    addMessage({
      type: 'bot',
      content: loadingText
    });
    
    // Helper to update the bot message
    const updateBotMessage = (text: string) => {
      // Update the message in context
      const { sessionMessages } = useConversationContext.getState();
      const updatedMessages = sessionMessages.map(msg => 
        msg.id === botMessageId ? { ...msg, content: text } : msg
      );
      
      useConversationContext.setState({ sessionMessages: updatedMessages });
      
      // Update the displayed message
      updateLastBotMessage(text);
    };
    
    // Start the loading animation
    const loadingInterval = setInterval(() => {
      updateBotMessage(`${loadingText}${loadingDots[dotIndex]}`);
      dotIndex = (dotIndex + 1) % loadingDots.length;
    }, 300);
    
    // Track the order
    const orderDetails = await trackOrder(cleanOrderNumber);
    
    // Clear the loading interval
    clearInterval(loadingInterval);
    
    // Respond with the order details or an error
    if (orderDetails) {
      const responseText = formatResponse(orderDetails);
      
      // Update the loading message with the response
      updateBotMessage('');
      
      // Simulate streaming for the response
      const words = responseText.split(' ');
      let currentText = '';
      
      for (let i = 0; i < words.length; i++) {
        currentText += (i > 0 ? ' ' : '') + words[i];
        updateBotMessage(currentText);
        await new Promise(resolve => setTimeout(resolve, 50)); // Delay between words
      }
      
      // Add the message to context after it's fully displayed
      addToContext({
        id: generateUUID(),
        type: 'bot',
        content: responseText,
        timestamp: new Date().toISOString()
      });
    } else {
      // No order found, use the translated error message or the custom error message from settings
      updateBotMessage('');
      
      // Use the translation if available, otherwise fall back to settings or default
      const errorMessage = currentLanguage !== 'en'
        ? t('orderTracking.errorMessage')
        : (orderTrackingSettings?.error_message || 
          "We couldn't find an order with that number. Please provide your email or phone number so we can look into this and contact you about your order.");
      
      // Simulate streaming for the error message
      const words = errorMessage.split(' ');
      let currentText = '';
      
      for (let i = 0; i < words.length; i++) {
        currentText += (i > 0 ? ' ' : '') + words[i];
        updateBotMessage(currentText);
        await new Promise(resolve => setTimeout(resolve, 50)); // Delay between words
      }
      
      // Add the message to context after it's fully displayed
      addToContext({
        id: generateUUID(),
        type: 'bot',
        content: errorMessage,
        timestamp: new Date().toISOString()
      });
      
      // Wait for user to provide contact info, then the AI will take over
    }
    
    // Reset the waiting state
    setWaitingForOrderNumber(false);
    setOrderTrackingMessageId(null);
    setMessageFullyTyped(false);
  };

  return (
    <div className="ml-8 space-y-3">
      {visibleApps.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {visibleApps
            .filter(app => app.id !== 'social') // Filter out the social app to avoid duplicate button
            .map((app) => (
              <MiniAppButton
                key={app.id}
                app={{
                  id: app.id,
                  label: app.label,
                  buttonVisible: true // We've already filtered for buttonVisible !== false
                }}
              />
            ))}
        </div>
      )}

      {/* Social Media Buttons - only show the networks, not a separate button */}
      {socialMediaEnabled && <SocialMediaButtons />}

      {allActions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {allActions.map((action, index) => (
            <QuickActionButton
              key={index}
              action={action}
              onClick={() => handleQuickAction(action.text, action.url)}
              theme={theme.mode}
            />
          ))}
        </div>
      )}

      {/* Order Tracking Input - Only show when message is fully typed */}
      {waitingForOrderNumber && orderTrackingMessageId && messageFullyTyped && (
        <OrderTrackingInput 
          messageId={orderTrackingMessageId}
          onSubmit={processOrderNumber}
        />
      )}
    </div>
  );
}